<script setup>
import { onMounted, ref } from 'vue';
import ModalForm from '../elements/ModalForm.vue';
import FileInput from '../elements/FileInput.vue';
import axios from 'axios';

const props = defineProps({
    // current subscription filter value from the parent dropdown:
    //   ''        -> "alle"        -> import warns first, then imports with null
    //   '__null'  -> "kein Mandant" -> imports with null
    //   <uuid>    -> specific Mandant -> imports with that subscription_id
    subscriptionFilter: {
        type: String,
        default: ''
    },
    sectorFilter: {
        type: String,
        default: ''
    },
    // map of subscription id -> name, used to label the resolved target in the modal
    subscriptionList: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['imported']);

const importFormId = 'prompt-snippet-import-modal';
const importWarnFormId = 'prompt-snippet-import-warn-modal';

const loading = ref(0);
const importErrors = ref({});
const importErrorMessage = ref('');
const fileInputRef = ref(null);

onMounted(() => {
    $('#' + importFormId).on('hidden.bs.modal', function () {
        importErrors.value = {};
        importErrorMessage.value = '';
        if (fileInputRef.value) {
            fileInputRef.value.reset();
        }
    });
});

function downloadFile(filePath, fileName) {
    const link = document.createElement('a');
    link.href = filePath;
    link.download = fileName;
    link.click();
}

function exportSnippets() {
    const params = new URLSearchParams();
    if (props.subscriptionFilter !== '') {
        params.append('subscription_id', props.subscriptionFilter);
    } else if (props.sectorFilter) {
        params.append('sector', props.sectorFilter);
    }
    const queryString = params.toString();
    downloadFile(
        '/promptSnippets/exportJson' + (queryString ? '?' + queryString : ''),
        'prompt-snippets.json'
    );
}

function showImportForm() {
    if (props.subscriptionFilter === '') {
        $('#' + importWarnFormId).modal();
    } else {
        $('#' + importFormId).modal();
    }
}

function onImportWarnConfirm() {
    $('#' + importWarnFormId).modal('hide');
    $('#' + importFormId).modal();
}

function onImportFormSubmit(submitEvent) {
    const formData = new FormData(submitEvent.target);
    // Resolve target subscription from the current filter. "" (alle) and
    // "__null" (kein Mandant) both mean: import with subscription_id = null.
    if (props.subscriptionFilter && props.subscriptionFilter !== '__null') {
        formData.set('subscription_id', props.subscriptionFilter);
    } else {
        formData.delete('subscription_id');
    }
    loading.value++;
    importErrors.value = {};
    importErrorMessage.value = '';
    axios.post('/ajax/promptSnippets/import', formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    })
        .then(() => {
            loading.value--;
            $('#' + importFormId).modal('hide');
            emit('imported');
        })
        .catch(function (error) {
            loading.value--;
            if (error.response && error.response.data) {
                if (error.response.data.errors) {
                    importErrors.value = error.response.data.errors;
                }
                if (error.response.data.message) {
                    importErrorMessage.value = error.response.data.message;
                }
            }
            console.error(error);
        });
}
</script>

<template>
    <span>
        <button class="btn btn-sm btn-info mr-2" type="button" @click.prevent="exportSnippets">exportieren</button>
        <button class="btn btn-sm btn-warning" type="button" @click.prevent="showImportForm">importieren</button>
    </span>

    <Teleport to="body">
        <div class="text-left">
            <ModalForm :modal-id="importWarnFormId" modal-title="Ohne Mandant importieren?"
                submit-text="trotzdem importieren" dismiss-text="abbrechen" :loading="loading"
                @form-submit="onImportWarnConfirm">
                Es ist kein Mandant ausgewählt. Die importierten Snippets werden <b>ohne Mandant</b> angelegt. Wollen
                Sie fortfahren?
            </ModalForm>

            <ModalForm :modal-id="importFormId" modal-title="Snippets importieren (JSON)" modal-class="modal-lg"
                submit-text="importieren" dismiss-text="abbrechen" :loading="loading" @form-submit="onImportFormSubmit">
                <FileInput ref="fileInputRef" name="list" label="Datei" accept=".json,.ndjson,application/json"
                    required />

                <div class="form-group">
                    <small class="text-muted">
                        Ziel-Mandant:
                        <span
                            v-if="subscriptionFilter && subscriptionFilter !== '__null' && subscriptionList[subscriptionFilter]">
                            <b>{{ subscriptionList[subscriptionFilter] }}</b>
                        </span>
                        <span v-else><b>kein Mandant</b></span>
                    </small>
                    <br />
                    <small class="text-muted" v-if="!subscriptionFilter || subscriptionFilter === '__null'">
                        Vorhandene Snippets werden anhand der UUID erkannt und aktualisiert; ihr Mandant bleibt dabei
                        unverändert.
                    </small>
                </div>
                <div class="invalid-feedback d-block" v-if="importErrors.list">
                    {{ importErrors.list[0] }}
                </div>
                <div class="invalid-feedback d-block" v-if="importErrors.subscription_id">
                    {{ importErrors.subscription_id[0] }}
                </div>
                <div class="alert alert-danger mt-2" v-if="importErrorMessage">
                    {{ importErrorMessage }}
                </div>
            </ModalForm>
        </div>
    </Teleport>
</template>
